import { Component, DestroyRef, OnInit, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { environment } from '@app/../config/environments/environment'
import { AuthService } from '@app/core/auth/services/auth.service'
import { allPermissions } from '@app/core/auth/permissions'
import { Client } from '@app/models/client.model'
import { DetailsService } from '@app/shared/pages/settings/services/details.service'

/** Shown in place of a real credential for users who may not see it. */
const CLIENT_ID_PLACEHOLDER = 'YOUR_CLIENT_ID'
const TOKEN_PLACEHOLDER = 'YOUR_API_TOKEN'

@Component({
  selector: 'app-api-reference',
  templateUrl: './api-reference.component.html',
  styleUrls: ['./api-reference.component.scss'],
  standalone: false,
})
export class ApiReferenceComponent implements OnInit {
  private destroyRef = inject(DestroyRef)

  readonly serverUrl = environment.serverUrl
  /**
   * The full URLs to POST to. Documenting only the base URL is how an integrator ends up on the
   * wrong host: the mobile API answers 404 for these paths, which reads as "the endpoint was
   * removed" rather than "you are talking to the wrong service".
   */
  readonly passesUrl = `${environment.serverUrl}/passes`
  readonly eventsUrl = `${environment.serverUrl}/events`
  readonly passesTestUrl = `${environment.serverUrl}/passes/test`

  copiedId: string | null = null

  /** The signed-in client's real values, so every example below is runnable as-is. */
  clientId: string = CLIENT_ID_PLACEHOLDER
  apiToken: string = TOKEN_PLACEHOLDER
  /**
   * `api_token` is a credential and the Help Center is NOT permission-gated — every signed-in
   * portal user reaches this page. So the token is substituted into the examples only for users
   * who can already read it in Settings -> Business Details; everyone else keeps the placeholder
   * and a pointer to that page.
   */
  canSeeToken = false

  constructor(
    private detailsService: DetailsService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.canSeeToken = (this.authService.user?.role?.permissions || []).includes(allPermissions.settings.groups.business_details.name)

    this.detailsService
      .getCurrentClient()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((client: Client) => {
        if (client?.id) this.clientId = String(client.id)
        if (this.canSeeToken && client?.api_token) this.apiToken = client.api_token
      })
  }

  // ---------------------------------------------------------------------------------------------
  // Examples — getters, not constants, so they pick up the signed-in client's credentials once
  // they load and a reader can copy any block and send it without editing anything.
  // ---------------------------------------------------------------------------------------------

  get eventsExample(): string {
    return `{
  "system_name": "MY_SYSTEM",
  "client_id": ${this.clientIdNumberLiteral},
  "token": "${this.apiToken}",
  "event_id": "EVT-2026-001",
  "event_name": "Annual Conference",
  "category": "culture",
  "opening_time": "2026-06-01T17:00:00",
  "starting_time": "2026-06-01T18:00:00",
  "closing_time": "2026-06-01T23:00:00",
  "allow_ticket_transfer": "true",
  "predefined_location_id": 42
}`
  }

  readonly eventsSuccessResponse = `{
  "status": "success",
  "message": "event created successfully",
  "event_number": 4521,
  "monitor_link": "https://monitor.yabi.cloud/abc123xyz..."
}`

  readonly eventsErrorResponse = `{
  "status": "error",
  "message": "missing argument"
}`

  get passExample(): string {
    return `{
  "system_name": "MY_SYSTEM",
  "client_id": "${this.clientId}",
  "token": "${this.apiToken}",
  "send_sms_new_pass": true,
  "passes": [
    {
      "event_id": "4521",
      "name": "Israel",
      "last_name": "Israeli",
      "id_number": "000000000",
      "mobile": "0501234567",
      "country_code": "972",
      "email": "0@0.0",
      "transaction": "TXN-001",
      "code": "_generate_",
      "allowed": 1,
      "secured": 0,
      "areas": "main entrance",
      "time_limitations": "{\\"range_allowed\\":\\"1746000000-1756000000\\"}",
      "transferable": true
    }
  ]
}`
  }

  /** The same pass with the areas coming from a client role, instead of repeating them per request. */
  get passByRoleExample(): string {
    return `{
  "system_name": "MY_SYSTEM",
  "client_id": "${this.clientId}",
  "token": "${this.apiToken}",
  "passes": [
    {
      "event_id": "4521",
      "name": "Israel",
      "last_name": "Israeli",
      "id_number": "000000000",
      "mobile": "0501234567",
      "country_code": "972",
      "email": "0@0.0",
      "transaction": "TXN-002",
      "code": "_generate_",
      "allowed": 1,
      "secured": 0,
      "client_role": "Guests SMS",
      "time_limitations": "{\\"range_allowed\\":\\"1746000000-1756000000\\"}"
    }
  ]
}`
  }

  readonly passSuccessResponse = `[
  {
    "status": "success",
    "country_code": "972",
    "mobile": "0501234567",
    "code": "eea9652557"
  }
]`

  /** What a fully successful request looks like when every pass in it is secured. Not an error. */
  readonly passSecuredResponse = `[]`

  readonly passErrorResponse = `[
  {
    "status": "error",
    "country_code": "972",
    "mobile": "0501234567",
    "message": "missing argument"
  }
]`

  get employeeExample(): string {
    return `{
  "system_name": "MY_SYSTEM",
  "client_id": "${this.clientId}",
  "token": "${this.apiToken}",
  "passes": [
    {
      "category": 10,
      "event_id": "4521",
      "name": "Israel",
      "last_name": "Israeli",
      "id_number": "123456789",
      "date_of_birth": "15-03-1985",
      "mobile": "0501234567",
      "country_code": "972",
      "email": "israel.israeli@company.com",
      "transaction": "EMP-001",
      "code": "_generate_",
      "allowed": 1,
      "secured": 1,
      "areas": "main entrance",
      "time_limitations": "{\\"range_allowed\\":\\"1646085600-0000000000\\"}",
      "wage_agreement": 1,
      "client_role": 1,
      "gender": "male",
      "day_off": "6,7",
      "address": "123 Main St, Tel Aviv",
      "family_status": "married",
      "remote_attendance": false,
      "attendance_manager": false,
      "create_pass": false
    }
  ]
}`
  }

  get areasUpdateExample(): string {
    return `{
  "system_name": "MY_SYSTEM",
  "client_id": "${this.clientId}",
  "token": "${this.apiToken}",
  "passes": [
    {
      "event_id": "4521",
      "mobile": "0501234567",
      "country_code": "972",
      "areas": "vip,backstage",
      "areas_add": true
    }
  ]
}`
  }

  /** A dry run against the real endpoint — the fastest way to prove the URL and credentials work. */
  get curlExample(): string {
    return `curl -i -X POST '${this.passesTestUrl}' \\
  -H 'Content-Type: application/json' \\
  -d '{"system_name":"MY_SYSTEM","client_id":"${this.clientId}","token":"${this.apiToken}","passes":[]}'`
  }

  readonly timeLimitationsBasic = `{"range_allowed":"1646085600-1716085600"}`

  readonly timeLimitationsAdvanced = `{
  "range_allowed": "1684616400-1685480400",
  "days_hours_allowed": {
    "1": ["1400-1600", "2000-2200"],
    "7": ["0000-2359"]
  }
}`

  /** `/events` takes `client_id` as a number, `/passes` as a string — keep both examples honest. */
  private get clientIdNumberLiteral(): string {
    return this.clientId === CLIENT_ID_PLACEHOLDER ? `"${this.clientId}"` : this.clientId
  }

  copyToClipboard(text: string, id: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.copiedId = id
      setTimeout((): void => {
        this.copiedId = null
      }, 2000)
    })
  }

  savePdf() {
    window.print()
  }

  scrollTo(id: string) {
    const target = document.getElementById(id)
    const container = document.querySelector('.api-main') as HTMLElement
    if (!target || !container) return
    container.scrollTo({ top: target.offsetTop - 16, behavior: 'smooth' })
  }
}
